import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '@/contexts/AuthContext';
import { AboboCrypto } from '@/utils/aboboCrypto';

const useTextDecryption = (encryptedData: string, encryptionKey: string) => {
  const [decryptedText, setDecryptedText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(true);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { patientPrivateKey, keysLoading } = useAuth();

  useEffect(() => {
    // If keys are still loading, keep processing state true (like abobo-dash)
    if (keysLoading) {
      setIsProcessing(true);
      setIsSuccess(false);
      return;
    }

    // If no key after loading completes, stop processing. This is the
    // "authenticated but no key" state — it's reported once by AuthContext when
    // `needsUnlock` becomes true (and handled by the unlock dialog), so we do
    // NOT capture per-message here (that would fire once per rendered message).
    if (!patientPrivateKey) {
      setIsProcessing(false);
      setIsSuccess(false);
      return;
    }

    if (!encryptedData || !encryptionKey) {
      setIsProcessing(false);
      setIsSuccess(false);
      setDecryptedText('');
      return;
    }

    setIsProcessing(true);

    const decryptData = async () => {
      try {
        const decodedEncryptedMessage = AboboCrypto.base64ToUint8Array(encryptedData);
        const decodedEncryptionKey = AboboCrypto.base64ToUint8Array(encryptionKey);

        const decryptedPayload = await AboboCrypto.decryptPayload(
          decodedEncryptedMessage,
          decodedEncryptionKey,
          patientPrivateKey
        );

        const decryptedText = new TextDecoder().decode(decryptedPayload);
        setDecryptedText(decryptedText);
        setIsSuccess(true);
      } catch (error) {
        setDecryptedText('');
        setIsSuccess(false);

        Sentry.captureException(error, {
          tags: { decryption_error: true },
          extra: {
            hasEncryptedData: !!encryptedData,
            hasEncryptionKey: !!encryptionKey,
            hasPrivateKey: !!patientPrivateKey,
            isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
            hasSubtleCrypto: typeof window !== 'undefined' ? !!window.crypto?.subtle : undefined,
          },
        });
      }
    };

    decryptData().finally(() => setIsProcessing(false));
  }, [patientPrivateKey, encryptedData, encryptionKey, keysLoading]);

  return { decryptedText, isProcessing, isSuccess, keysLoading };
};

export default useTextDecryption;
